import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Shield, CheckCircle, XCircle, Copy, Download } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";

export default function MFASetup() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [verificationCode, setVerificationCode] = useState("");
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [showBackupCodes, setShowBackupCodes] = useState(false);

  // Get MFA status
  const { data: mfaStatus, isLoading: statusLoading } = useQuery<{ enabled: boolean }>({
    queryKey: ["/api/mfa/status"],
  });

  // Setup MFA mutation
  const setupMutation = useMutation({
    mutationFn: async () => {
      return apiRequest("/api/mfa/setup", { method: "POST" });
    },
    onSuccess: (data) => {
      setBackupCodes(data.backupCodes || []);
      setShowBackupCodes(true);
      toast({
        title: "MFA Setup Started",
        description: "Scan the QR code with your authenticator app, then enter the code to enable MFA.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Setup Failed",
        description: error.message || "Failed to setup MFA. Please try again.",
        variant: "destructive",
      });
    },
  });

  // Enable MFA mutation
  const enableMutation = useMutation({
    mutationFn: async (code: string) => {
      return apiRequest("/api/mfa/enable", { method: "POST", body: { code } });
    },
    onSuccess: () => {
      toast({
        title: "MFA Enabled",
        description: "Multi-factor authentication has been enabled successfully.",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/mfa/status"] });
      setVerificationCode("");
      setShowBackupCodes(false);
    },
    onError: (error: any) => {
      toast({
        title: "Verification Failed",
        description: error.message || "Invalid code. Please try again.",
        variant: "destructive",
      });
      setVerificationCode("");
    },
  });

  // Disable MFA mutation
  const disableMutation = useMutation({
    mutationFn: async () => {
      return apiRequest("/api/mfa/disable", { method: "POST" });
    },
    onSuccess: () => {
      toast({
        title: "MFA Disabled",
        description: "Multi-factor authentication has been disabled.",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/mfa/status"] });
    },
    onError: (error: any) => {
      toast({
        title: "Disable Failed",
        description: error.message || "Failed to disable MFA. Please try again.",
        variant: "destructive",
      });
    },
  });

  const handleSetup = () => {
    setupMutation.mutate();
  };

  const handleEnable = (e: React.FormEvent) => {
    e.preventDefault();
    if (verificationCode.length !== 6) {
      toast({
        title: "Invalid Code",
        description: "Please enter a 6-digit code.",
        variant: "destructive",
      });
      return;
    }
    enableMutation.mutate(verificationCode);
  };

  const handleDisable = () => {
    if (confirm("Are you sure you want to disable MFA? This will make your account less secure.")) {
      disableMutation.mutate();
    }
  };

  const copyBackupCodes = () => {
    navigator.clipboard.writeText(backupCodes.join("\n"));
    toast({
      title: "Copied",
      description: "Backup codes copied to clipboard.",
    });
  };

  const downloadBackupCodes = () => {
    const blob = new Blob([backupCodes.join("\n")], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "domain-iq-backup-codes.txt";
    a.click();
    URL.revokeObjectURL(url);
  };

  if (statusLoading) {
    return (
      <div className="space-y-6">
        <div className="h-32 bg-muted rounded animate-pulse" />
      </div>
    );
  }

  const isMFAEnabled = mfaStatus?.enabled === true;
  const setupData = setupMutation.data;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Multi-Factor Authentication</h1>
        <p className="text-muted-foreground">
          Add an extra layer of security to your account with Google Authenticator
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            MFA Status
          </CardTitle>
          <CardDescription>
            {isMFAEnabled
              ? "Multi-factor authentication is currently enabled for your account."
              : "Multi-factor authentication is not enabled. Set it up to secure your account."}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Current Status */}
          <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
            <div className="flex items-center gap-3">
              {isMFAEnabled ? (
                <>
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  <span className="font-medium">MFA Enabled</span>
                </>
              ) : (
                <>
                  <XCircle className="h-5 w-5 text-gray-400" />
                  <span className="font-medium">MFA Disabled</span>
                </>
              )}
            </div>
            <Badge variant={isMFAEnabled ? "default" : "secondary"}>
              {isMFAEnabled ? "Active" : "Inactive"}
            </Badge>
          </div>

          {/* Setup Flow */}
          {!isMFAEnabled && !setupData && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                To enable MFA, you'll need to:
              </p>
              <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground">
                <li>Scan the QR code with Google Authenticator or similar app</li>
                <li>Enter the 6-digit code to verify setup</li>
                <li>Save your backup codes in a secure location</li>
              </ol>
              <Button onClick={handleSetup} disabled={setupMutation.isPending}>
                {setupMutation.isPending ? "Setting up..." : "Setup MFA"}
              </Button>
            </div>
          )}

          {/* QR Code and Verification */}
          {setupData && !isMFAEnabled && (
            <div className="space-y-4">
              <div className="text-center">
                <p className="text-sm font-medium mb-4">Scan this QR code with your authenticator app:</p>
                <div className="flex justify-center p-4 bg-white rounded-lg border">
                  <img 
                    src={setupData.qrCodeUrl} 
                    alt="MFA QR Code" 
                    className="w-64 h-64"
                  />
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Or enter this secret manually: <code className="bg-muted px-1 rounded">{setupData.secret}</code>
                </p>
              </div>

              <form onSubmit={handleEnable} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="verify-code">Enter 6-digit code to verify</Label>
                  <Input
                    id="verify-code"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]{6}"
                    maxLength={6}
                    placeholder="000000"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ''))}
                    className="text-center text-2xl tracking-widest"
                    required
                  />
                </div>
                <Button 
                  type="submit" 
                  disabled={enableMutation.isPending || verificationCode.length !== 6}
                  className="w-full"
                >
                  {enableMutation.isPending ? "Verifying..." : "Enable MFA"}
                </Button>
              </form>

              {/* Backup Codes */}
              {showBackupCodes && backupCodes.length > 0 && (
                <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
                  <p className="text-sm font-medium text-yellow-900 dark:text-yellow-100 mb-2">
                    ⚠️ Save these backup codes!
                  </p>
                  <p className="text-xs text-yellow-800 dark:text-yellow-200 mb-3">
                    These codes can be used to access your account if you lose your authenticator device.
                    Each code can only be used once.
                  </p>
                  <div className="grid grid-cols-2 gap-2 mb-3">
                    {backupCodes.map((code, index) => (
                      <code key={index} className="p-2 bg-white dark:bg-slate-800 rounded text-sm font-mono">
                        {code}
                      </code>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={copyBackupCodes}
                    >
                      <Copy className="h-4 w-4 mr-2" />
                      Copy
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={downloadBackupCodes}
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Download
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Disable MFA */}
          {isMFAEnabled && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Your account is protected with multi-factor authentication.
              </p>
              <Button 
                variant="destructive" 
                onClick={handleDisable}
                disabled={disableMutation.isPending}
              >
                {disableMutation.isPending ? "Disabling..." : "Disable MFA"}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}


